/**
 * Links the user's login connection to the Auth0 Token Vault, so the server can
 * refresh their identity provider token later instead of reusing the one
 * captured at login.
 *
 * The server asks for this (`token_vault_seed` on the token mint response) when
 * the organization has refresh enabled and Auth0 holds no refresh token for the
 * user yet. Auth0 offers a few ways to get one vaulted; the Connected Accounts
 * handshake is the one that fits a browser holding a user session, and it needs a
 * trip to the identity provider:
 *
 * 1. mint a My Account API token in the browser (the grant is user-delegated)
 * 2. ask Auth0 to start the handshake, which returns a one-time ticket
 * 3. send the browser to the identity provider with that ticket
 * 4. hand the code from the callback back to Auth0, which vaults the token
 *
 * With a live session at the identity provider steps 3 and 4 are two silent
 * redirects; Google additionally shows an account picker and one consent screen.
 * Anything that goes wrong leaves the user logged in on the login-time token,
 * which is how Superblocks behaves without this feature at all.
 */

import type { TokenVaultSeedRequest } from "@superblocksteam/shared";

import AuthProvider from "core/auth/auth0";
import { uiLogger } from "core/telemetry/logger";

import { recordSeedStep } from "./tokenVaultSeedMetrics";

/**
 * Set at the moment we hand the browser to the identity provider, and never
 * cleared, so a trip that comes back without a code cannot turn into a redirect
 * loop. A new tab tries again.
 *
 * Deliberately not set for a failure that happened before that point: a token
 * Auth0 would not mint, or a handshake it would not start, navigated nowhere and
 * is safe to retry on the next mint.
 */
const ATTEMPTED_KEY = "superblocks.tokenVaultSeed.attempted";
/**
 * The user declined at the identity provider's consent screen. Unlike the
 * per-tab marker above this one persists, because that answer does not change on
 * a retry, and asking again once per new tab would bounce them out to the
 * provider and back on every tab they ever open.
 *
 * Only their refusal is remembered. The provider reports a request we built
 * wrong down the same channel, and treating that as a refusal would lock a user
 * out of seeding long after the fix shipped, until they cleared browser storage
 * by hand.
 *
 * Recorded against the person as well as the connection, because a browser can
 * be shared. Keyed on the connection alone, one person declining on a shared
 * or hot-desk machine would silently skip seeding for everyone who signs in
 * there afterwards, which is not what they answered.
 */
const REFUSED_KEY = "superblocks.tokenVaultSeed.refused";

/** Whose answer, on which connection. Both, so one does not stand in for the other. */
function refusalId(seed: TokenVaultSeedRequest): string {
  return `${seed.connection}|${seed.login_hint}`;
}

/**
 * One key per refusal, rather than one key holding the most recent one.
 *
 * A single slot means a second person, or a second connection, on the same
 * browser profile overwrites the first refusal, and whoever refused first gets
 * sent back through the consent screen they already declined. The point of this
 * marker is that no stays no, so it has to remember every no. Bounded by the
 * people who actually refuse in this profile, which is why nothing prunes it.
 */
function refusedKey(id: string): string {
  return `${REFUSED_KEY}.${id}`;
}
/** The one error that means the user said no, rather than us asking wrongly. */
const USER_DECLINED = "access_denied";
/**
 * The error codes OAuth 2.0 and OpenID Connect define for an authorize response.
 * Whatever comes back on the callback is whatever the upstream provider chose to
 * send, so anything outside this list is counted as one bucket rather than
 * minting a new metric series per string a provider invents.
 */
const OAUTH_ERRORS = new Set([
  "access_denied",
  "account_selection_required",
  "consent_required",
  "interaction_required",
  "invalid_request",
  "invalid_scope",
  "login_required",
  "server_error",
  "temporarily_unavailable",
  "unauthorized_client",
  "unsupported_response_type",
]);

function knownOauthError(code: string): string {
  return OAUTH_ERRORS.has(code) ? code : "other";
}
/**
 * Auth0's answers when it will not authorize silently, which for us both mean the
 * session has aged past the window right after a login.
 *
 * `unmet_authentication_requirements` is the one seen in practice, and its
 * description says why the silent path cannot recover: "cannot be challenged".
 * Raising the session's assurance needs a prompt, and a silent authorize forbids
 * prompting by definition, so this answer does not change until the next login.
 */
const SESSION_TOO_OLD = new Set([
  "login_required",
  "unmet_authentication_requirements",
]);
const PENDING_KEY = "superblocks.tokenVaultSeed.pending";
/**
 * What the My Account API token has to carry. `offline_access` is the one that is
 * easy to miss and not obviously ours to ask for: it is the connect call's own
 * mandate to keep a refresh token, separate from the offline access the upstream
 * connection needs. Without it Auth0 answers a completed handshake with "Missing
 * refresh token. Connected accounts requires offline access to function properly",
 * which reads like the identity provider's fault and is not.
 *
 * `delete:me:connected_accounts` is deliberately left out even though the docs
 * list it: nothing here unlinks an account, and the tenant grant is a ceiling
 * rather than a requirement.
 */
const MY_ACCOUNT_SCOPES =
  "openid profile offline_access create:me:connected_accounts read:me:connected_accounts";
/**
 * The caller holds the login spinner while this runs, so the whole thing has to
 * be bounded. Auth0 degrading into slow-but-not-failing is the case this covers:
 * without it a hung silent authorize or a hung POST would hold the spinner
 * indefinitely and produce no signal at all.
 *
 * One budget for the mint and the request together, not one each, so this really
 * is the longest the spinner can be held.
 */
const HANDSHAKE_TIMEOUT_MS = 5_000;

type PendingSeed = {
  authSession: string;
  connection: string;
  redirectUri: string;
  refusalId: string;
  returnUrl: string;
  state: string;
};

/**
 * One attempt at a time across the whole page. `ATTEMPTED_KEY` is only written
 * once Auth0 has issued a ticket, so two mints that overlap - a view mode
 * change, a token refetch, the app and dashboard trees both mounting - would
 * each start a handshake of their own and each try to navigate.
 */
let seedInFlight: Promise<void> | undefined;

/**
 * The documented anti-forgery value on the connect request: Auth0 hands it back
 * on the callback so we can tell our own handshake apart from a link someone else
 * started in this browser. `auth_session` already binds the two calls, so this is
 * the belt to that braces.
 *
 * Checked only when Auth0 actually echoes it. Auth0 documents `state` as required
 * on the request and says nothing about the callback, and a strict comparison
 * would turn a parameter it chose not to return into a broken link on a
 * connection that works today.
 */
function newState(): string {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Runs on its own while the page loads, so it can only take the token Auth0 will
 * hand over without involving the user. That works while the session is new, which
 * is the pass right after a login, and Auth0 declines once the session has aged.
 * Declining is fine: the user keeps the token they logged in with, and the next
 * login tries again.
 */
export async function startTokenVaultSeed({
  domain,
  seed,
}: {
  domain: string;
  seed: TokenVaultSeedRequest;
}): Promise<void> {
  recordSeedStep("requested");
  // Embedded Superblocks lives in someone else's page; never navigate it away.
  if (window.self !== window.top) {
    recordSeedStep("skipped", { reason: "embedded" });
    return;
  }
  if (window.sessionStorage.getItem(ATTEMPTED_KEY)) {
    recordSeedStep("skipped", { reason: "already_attempted" });
    return;
  }
  if (window.localStorage.getItem(refusedKey(refusalId(seed))) !== null) {
    recordSeedStep("skipped", { reason: "refused_before" });
    return;
  }
  if (seedInFlight) {
    // A second caller for the same handshake rather than a second handshake. Both
    // `useJwt` trees can mount, Strict Mode double-invokes, and a view-mode
    // remint calls in again. Counted, because `requested` is recorded per call
    // and an uncounted coalesce reads as a drop between `requested` and
    // `started` - which is the one thing this funnel exists to measure.
    recordSeedStep("skipped", { reason: "already_in_flight" });
    await seedInFlight;
    return;
  }
  seedInFlight = runSeed({ domain, seed }).finally(() => {
    seedInFlight = undefined;
  });
  await seedInFlight;
}

/**
 * Call on the way out. The markers above guard one session's attempt at linking,
 * and logging out ends that session: the next person to sign in, or the same
 * person signing in again, deserves a fresh attempt rather than inheriting a
 * marker set before they arrived.
 *
 * A refusal is deliberately kept. That is the user's answer, not a state of the
 * attempt, and it lives in local storage precisely so it survives this.
 */
export function forgetTokenVaultSeedAttempt(): void {
  window.sessionStorage.removeItem(ATTEMPTED_KEY);
  window.sessionStorage.removeItem(PENDING_KEY);
}

async function runSeed({
  domain,
  seed,
}: {
  domain: string;
  seed: TokenVaultSeedRequest;
}): Promise<void> {
  const returnUrl = window.location.href;
  const redirectUri = window.location.origin;
  const state = newState();
  try {
    // No `scopes`: omitting it leaves Auth0 using each connection's own configured
    // scopes, which is the only thing that is right for every provider at once.
    // Okta only returns a refresh token when `offline_access` is asked for; Google
    // refuses that scope outright. Both facts already live on the connection, and
    // nothing in a mint request reliably says which provider is upstream - a
    // connection named anything at all can federate to Google.
    const connected = await callMyAccount({
      body: {
        authorization_params: { login_hint: seed.login_hint },
        connection: seed.connection,
        redirect_uri: redirectUri,
        state,
      },
      domain,
      path: "connect",
    });
    // Forward every parameter Auth0 returned, not just the ticket. It hands back a
    // bag of them on purpose, and dropping any leaves the request incomplete, which
    // Auth0 answers by ending the session rather than by refusing the link.
    const ticketUrl = new URL(connected.connect_uri);
    for (const [name, value] of Object.entries(connected.connect_params)) {
      ticketUrl.searchParams.set(name, String(value));
    }

    const pending: PendingSeed = {
      authSession: connected.auth_session,
      connection: seed.connection,
      redirectUri,
      refusalId: refusalId(seed),
      returnUrl,
      state,
    };
    window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    // Set here, not before the attempt: this is the point where we hand the
    // browser to the provider and stop being able to see what happens. If it comes
    // back without a code the server still reports an empty vault, quite correctly,
    // and asks again - so without this the pair would bounce forever. Everything
    // before this line either failed without navigating or succeeded, and both are
    // safe to try again on the next mint.
    window.sessionStorage.setItem(ATTEMPTED_KEY, "true");
    recordSeedStep("started");
    uiLogger.info("Sending the browser to link the Token Vault connection", {
      "vault.connection": seed.connection,
      // Where Auth0 said to send them, without the ticket, which is a one-time
      // credential. When a link fails the browser never comes back, so this is the
      // last thing we know about it: a host that is not the identity provider's
      // says the handshake went wrong before the user ever saw a consent screen.
      "vault.seed.destination": `${ticketUrl.origin}${ticketUrl.pathname}`,
      // Names only. Auth0 documents just the ticket, so anything else here is a
      // parameter we would have dropped before.
      "vault.seed.forwarded": Object.keys(connected.connect_params)
        .sort()
        .join(","),
    });
    window.location.assign(ticketUrl.toString());
  } catch (error) {
    const oauthCode = readOauthCode(error);
    // A session Auth0 will not authorize silently does not become authorizable
    // again without a new login, and the server keeps asking on every mint, so
    // without this a deployed app re-minting every few minutes would attempt a
    // hidden authorize every few minutes for the whole session, forever. Only
    // this one code: any other failure is worth retrying on the next mint.
    if (oauthCode !== undefined && SESSION_TOO_OLD.has(oauthCode)) {
      window.sessionStorage.setItem(ATTEMPTED_KEY, "true");
    }
    // Auth0 refusing the token and Auth0 refusing the handshake are different
    // problems with different owners, and the funnel is only useful if it tells
    // them apart.
    recordSeedStep(oauthCode ? "token_refused" : "connect_refused", {
      reason: oauthCode ?? readMyAccountFailure(error),
    });
    uiLogger.warn("Could not start linking the Token Vault connection", {
      "error.message": String(error),
      "vault.connection": seed.connection,
      ...(oauthCode ? { "vault.seed.error_code": oauthCode } : {}),
    });
  }
}

/**
 * The `connected-accounts/...` errors this module throws carry their status and
 * Auth0's own code, both from a fixed set, so they are safe as a metric label. Any
 * other failure is reduced to one bucket rather than risking free text.
 */
function readMyAccountFailure(error: unknown): string {
  const message = error instanceof Error ? error.message : "";
  const match = /connected-accounts\/\w+ returned (\d{3})\s*(\w*)/.exec(
    message,
  );
  if (!match) {
    return "unknown";
  }
  return match[2] ? `${match[1]}_${match[2]}` : match[1];
}

/**
 * Comes from a silent authorize against the user session, which works while the
 * session is new - the pass right after a login - and is declined once it has aged
 * (`unmet_authentication_requirements` on the dev tenant).
 *
 * Not an Auth0 limitation: multi-resource refresh tokens exist to mint a second
 * audience from one refresh token. The SDK here cannot use them - spa-js 1.22.6
 * keys refresh tokens by audience, so a second-audience request finds none and
 * falls back to this same iframe - so the aged-session, Safari and private-window
 * cases wait on an SDK major upgrade rather than on anything Auth0 does.
 */
function mintMyAccountToken(origin: string, deadline: number): Promise<string> {
  return withTimeout(
    AuthProvider.generateToken({
      audience: `${origin}/me/`,
      scope: MY_ACCOUNT_SCOPES,
    }),
    "minting a My Account token",
    deadline,
  );
}

/**
 * The OAuth error code the Auth0 SDK hangs off a rejection, which names the cause
 * where the message alone does not: a silent authorize that cannot use the
 * session fails as `login_required`, and a browser withholding the session cookie
 * from the iframe looks exactly the same. Without this field the only way to tell
 * why seeding never started is to read the Auth0 tenant log.
 */
function readOauthCode(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null || !("error" in error)) {
    return undefined;
  }
  return typeof error.error === "string" ? error.error : undefined;
}

/**
 * Whether this page load is the Connected Accounts callback coming back.
 *
 * The Auth0 SDK decides a login is coming back by looking for `code` or `error`
 * in the address bar, and a user who declines at the provider returns carrying
 * `error`. Left to itself the SDK then tries to finish a login that never
 * started, fails on a state it has no record of, and puts the "Login Failed"
 * page in front of someone whose session is perfectly good. Telling it to skip
 * the redirect callback leaves it on `checkSession`, which is what every other
 * page load does anyway.
 *
 * Decided from the pending record and not from the address bar alone: a real
 * failed login carries the same `error`, and the record exists only between
 * handing the browser to the provider and the very next load.
 */
export function isTokenVaultSeedCallback(): boolean {
  if (!readPendingSeed()) {
    return false;
  }
  const params = readCallbackParams();
  return params.has("connect_code") || params.has("error");
}

export async function completeTokenVaultSeed({
  domain,
}: {
  domain: string;
}): Promise<void> {
  // Both callers fire this and forget it, on every page load, so it swallows its
  // own failures rather than leaving an unhandled rejection behind. Storage a
  // browser refuses to hand over is the realistic one: everything here reads
  // session storage before it does anything else.
  try {
    await runComplete(domain);
  } catch (error) {
    uiLogger.warn("Could not check for a Token Vault link to finish", {
      "error.message": String(error),
    });
  }
}

async function runComplete(domain: string): Promise<void> {
  const pending = readPendingSeed();
  if (!pending) {
    return;
  }
  // The handshake navigates to the identity provider straight after writing this
  // record, so the very next load is the callback or the handshake was abandoned
  // (Back out of the consent screen, a closed tab). Either way there is nothing
  // left to complete, and leaving the record behind would make a later page that
  // happens to carry an `error` query param look like the callback.
  window.sessionStorage.removeItem(PENDING_KEY);

  const params = readCallbackParams();
  const connectCode = params.get("connect_code");
  const refusal = params.get("error");
  if (!connectCode && !refusal) {
    return;
  }
  // Only when Auth0 echoed it: a callback carrying someone else's state is not our
  // handshake, and redeeming its code would link an account this tab never asked
  // for. Auth0 documents `state` on the request and not on the callback, so its
  // absence has to stay acceptable.
  const echoedState = params.get("state");
  if (echoedState !== null && echoedState !== pending.state) {
    recordSeedStep("refused", { reason: "state_mismatch" });
    uiLogger.warn(
      "Ignored a Token Vault callback that belongs to another handshake",
      {
        "vault.connection": pending.connection,
      },
    );
    // Refusing to redeem the code is the point; parking the user on the callback
    // URL with it still in the address bar is not. Same exit as every other
    // path, because the pending record is already gone either way.
    window.location.replace(sameOriginUrl(pending.returnUrl));
    return;
  }
  if (refusal) {
    if (refusal === USER_DECLINED) {
      try {
        window.localStorage.setItem(refusedKey(pending.refusalId), "true");
      } catch {
        // The redirect back is further down this same path, so a browser that
        // refuses the write must not cost the user their way out of the
        // callback URL. They get asked once more next time instead.
      }
    }
    recordSeedStep("refused", { reason: knownOauthError(refusal) });
    uiLogger.warn("Linking the Token Vault connection was refused", {
      "vault.connection": pending.connection,
      // The code as sent, unlike the metric label: a log line takes an unfamiliar
      // one without cost, and it is the only place the upstream provider's own
      // wording survives. The description can carry account details, so it stays
      // out.
      "vault.seed.reason": refusal,
    });
  }

  if (connectCode) {
    try {
      await callMyAccount({
        body: {
          auth_session: pending.authSession,
          connect_code: connectCode,
          redirect_uri: pending.redirectUri,
        },
        domain,
        path: "complete",
      });
      recordSeedStep("linked");
      uiLogger.info("Linked the Token Vault connection", {
        "vault.connection": pending.connection,
      });
    } catch (error) {
      recordSeedStep("complete_refused", {
        reason: readMyAccountFailure(error),
      });
      uiLogger.warn("Could not finish linking the Token Vault connection", {
        "error.message": String(error),
        "vault.connection": pending.connection,
      });
    }
  }

  // Reloading also drops `connect_code` from the address bar and mints a token
  // that carries the now-refreshable identity provider token.
  window.location.replace(sameOriginUrl(pending.returnUrl));
}

type ConnectResponse = {
  auth_session: string;
  connect_params: Record<string, string>;
  connect_uri: string;
};

async function callMyAccount({
  body,
  domain,
  path,
}: {
  body: Record<string, unknown>;
  domain: string;
  path: string;
}): Promise<ConnectResponse> {
  const origin = myAccountOrigin(domain);
  const deadline = Date.now() + HANDSHAKE_TIMEOUT_MS;
  const token = await mintMyAccountToken(origin, deadline);
  const response = await fetch(`${origin}/me/v1/connected-accounts/${path}`, {
    body: JSON.stringify(body),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    method: "POST",
    signal: AbortSignal.timeout(Math.max(deadline - Date.now(), 1)),
  });
  if (!response.ok) {
    // Auth0's own error code is the only thing that separates "the grant is not
    // provisioned yet" from a real break, and it is in the body, not the status.
    throw new Error(
      `connected-accounts/${path} returned ${response.status} ${await readErrorCode(response)}`,
    );
  }
  return await response.json();
}

async function readErrorCode(response: Response): Promise<string> {
  try {
    const body = await response.json();
    return typeof body?.error === "string" ? body.error : "";
  } catch {
    return "";
  }
}

function withTimeout<T>(
  work: Promise<T>,
  label: string,
  deadline: number,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  return Promise.race([
    work,
    new Promise<never>((_resolve, reject) => {
      timer = setTimeout(
        () => reject(new Error(`${label} timed out`)),
        Math.max(deadline - Date.now(), 1),
      );
    }),
  ]).finally(() => clearTimeout(timer));
}

/** The configured Auth0 domain is a bare host in every environment. */
function myAccountOrigin(domain: string): string {
  return new URL(domain.includes("://") ? domain : `https://${domain}`).origin;
}

/**
 * Auth0 puts the callback parameters on the query string, verified end to end on
 * the dev tenant. Its own walkthrough says the fragment instead, and the flow
 * diagram beside it says neither, so read both.
 *
 * Worth the few lines because getting this wrong fails silently and permanently:
 * `runComplete` deletes the pending record before it looks, so a handshake whose
 * code we could not find is never retried - it just never links, with nothing in
 * the funnel to say why.
 */
function readCallbackParams(): URLSearchParams {
  const query = new URLSearchParams(window.location.search);
  const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  for (const [name, value] of fragment) {
    if (!query.has(name)) {
      query.set(name, value);
    }
  }
  return query;
}

/**
 * Never throws. One caller runs during a render, where a browser that refuses
 * session storage would otherwise take the whole page down instead of just
 * skipping a feature.
 */
function readPendingSeed(): PendingSeed | undefined {
  try {
    const stored = window.sessionStorage.getItem(PENDING_KEY);
    return stored ? JSON.parse(stored) : undefined;
  } catch {
    return undefined;
  }
}

function sameOriginUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin === window.location.origin) {
      return parsed.toString();
    }
  } catch {
    // fall through to the origin root
  }
  return `${window.location.origin}/`;
}
